import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, Clock, ChevronLeft, Check, Upload, FileText, User2, CalendarDays, Video, Phone, MapPin } from "lucide-react";
import { toast } from "sonner";

const METHOD_ICON = { phone: Phone, teams: Video, in_person: MapPin, custom: Clock };
const iso = (d) => d.toISOString().slice(0, 10);
const VIEW_TZ = Intl.DateTimeFormat().resolvedOptions().timeZone;
const EMPTY_LEAD = { first_name: "", last_name: "", email: "", mobile: "", city: "", state: "", interested_in: "" };

// Authenticated recruiter-assisted booking. Order: Person -> Appointment Type -> Time -> required fields -> Confirm.
// Host = page owner (calendar + Teams); Booked By = logged-in user; Attendee = selected Person.
// Non-group bookings use the server-authorized /booking/* endpoints (Booking Access enforced server-side).
export default function RecruiterBooking({ page, onClose, onBooked, inline = false, initialTypeId = "" }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const group = !!page.group;
  const types = (page.appointment_types || []).filter((t) => t.active !== false);
  const [type, setType] = useState(types.find((t) => t.id === initialTypeId) || (types.length === 1 ? types[0] : null));
  const [hosts, setHosts] = useState([]);
  const [hostId, setHostId] = useState("");
  const [date, setDate] = useState(new Date());
  const [slots, setSlots] = useState(null);
  const [slot, setSlot] = useState(null);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [person, setPerson] = useState(null);
  const [ctxData, setCtxData] = useState(null);
  const [email, setEmail] = useState("");
  const [notes, setNotes] = useState("");
  const [interest, setInterest] = useState("");
  const [resumeMode, setResumeMode] = useState("existing");
  const [resumeId, setResumeId] = useState(null);
  const [resumeName, setResumeName] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [addLead, setAddLead] = useState(false);
  const [showUnavail, setShowUnavail] = useState(false);
  const [grid, setGrid] = useState([]);
  const [overrideOk, setOverrideOk] = useState(false);
  const [leadForm, setLeadForm] = useState(EMPTY_LEAD);
  const fileRef = useRef(null);
  const tz = page.timezone || VIEW_TZ;
  const canOverride = !group && page.can_override !== false;

  useEffect(() => {
    if (!group || !page.organization_id) return;
    api.get("/group-scheduling/eligible-hosts", { params: { organization_id: page.organization_id } })
      .then((r) => setHosts(r.data.hosts || r.data || [])).catch(() => setHosts([]));
  }, [group, page.organization_id]);

  useEffect(() => {
    if (!type || !person || (group && !hostId)) { setSlots(null); setSlot(null); return; }
    setSlots(null); setSlot(null);
    const url = group ? `/group-scheduling/pages/${page.id}/slots-internal` : "/booking/slots";
    const params = { appointment_type_id: type.id, date: iso(date), tz };
    if (group) params.host_id = hostId; else params.page_id = page.id;
    if (canOverride && showUnavail) params.include_unavailable = true;
    api.get(url, { params }).then((r) => { setSlots(r.data.slots); setGrid(r.data.grid || []); })
      .catch((e) => { toast.error(apiError(e)); setSlots([]); setGrid([]); });
  }, [type, person, date, page.id, group, hostId, showUnavail, canOverride, tz]);

  useEffect(() => {
    if (!q || q.length < 2) { setResults([]); return; }
    const id = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = group
          ? await api.get("/persons", { params: { q, page_size: 8, all_districts: true } })
          : await api.get("/booking/people", { params: { q } });
        setResults(data.persons || []);
      } catch { setResults([]); } finally { setSearching(false); }
    }, 300);
    return () => clearTimeout(id);
  }, [q, group]);

  const pickPerson = async (p) => {
    setPerson(p); setResults([]); setQ("");
    try {
      const { data } = await api.get("/booking/person-context", { params: { person_id: p.id } });
      setCtxData(data);
      setEmail(data.emails?.[0] || p.email || "");
      setInterest(data.interested_in || "");
      setResumeMode(data.resume ? "existing" : "new");
      setResumeId(null); setResumeName(data.resume?.filename || "");
    } catch (e) { toast.error(apiError(e)); setPerson(null); }
  };

  const createLead = async () => {
    const f = leadForm;
    if (!f.first_name.trim() || !f.last_name.trim()) return toast.error("First and last name are required.");
    if (!f.email.trim()) return toast.error("Email is required.");
    if (!f.interested_in) return toast.error("Please select an Interested In option.");
    setBusy(true);
    try {
      const { data } = await api.post("/leads", { ...f });
      if (data.status === "created" || (data.status === "existing" && data.person_id)) {
        toast[data.status === "existing" ? "info" : "success"](
          data.status === "existing" ? "Found an existing record — using it to avoid a duplicate." : "Lead added");
        setAddLead(false); setLeadForm(EMPTY_LEAD);
        await pickPerson({ id: data.person_id, first_name: f.first_name, last_name: f.last_name, email: f.email });
      } else {
        toast.error(data.message || "Could not add this lead.");
      }
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const uploadResume = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) { toast.error("File too large (max 25 MB)."); return; }
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file); fd.append("person_id", person.id);
      const { data } = await api.post("/booking/upload-resume", fd);
      setResumeId(data.attachment_id); setResumeName(data.filename); setResumeMode("new");
      toast.success("Resume uploaded");
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  const book = async () => {
    const isUnavail = slot?.status && slot.status !== "available";
    setBusy(true);
    try {
      const body = {
        appointment_type_id: type.id, person_id: person.id, start: slot.start,
        attendee_email: email, notes, interested_in: interest,
        resume_attachment_id: resumeMode === "new" ? resumeId : undefined,
        use_existing_resume: resumeMode === "existing" && !!ctxData?.resume,
        override: !!isUnavail, book_anyway: slot?.status === "conflict",
      };
      let data;
      if (group) { body.host_id = hostId; ({ data } = await api.post(`/group-scheduling/pages/${page.id}/book-internal`, body)); }
      else { body.page_id = page.id; ({ data } = await api.post("/booking/book", body)); }
      setDone(data); onBooked?.();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const bookedByName = user?.name || [user?.first_name, user?.last_name].filter(Boolean).join(" ") || "You";
  const interestOptions = page.interest_options || [];
  const fr = type?.field_rules || {};
  const notesRequired = fr.notes === "required", notesHidden = fr.notes === "hidden";
  const resumeRequired = fr.resume === "required", resumeHidden = fr.resume === "hidden";
  const interestRequired = fr.interested_in === "required", interestHidden = fr.interested_in === "hidden";
  const hasResume = (resumeMode === "existing" && !!ctxData?.resume) || (resumeMode === "new" && !!resumeId);
  const notesOk = !notesRequired || notes.trim().length >= 20;
  const resumeOk = !resumeRequired || hasResume;
  const interestOk = !interestRequired || !!interest;
  const slotUnavail = !!(slot && slot.status && slot.status !== "available");
  const eligibleHosts = group && type ? hosts.filter((h) => (type.eligible_host_ids || []).includes(h.id)) : [];
  const hostName = group ? (eligibleHosts.find((h) => h.id === hostId)?.name || "—") : (page.branding?.display_name || page.name);
  const canBook = type && slot && person && email && !busy && (!group || hostId) && notesOk && resumeOk && interestOk && (!slotUnavail || overrideOk);
  const visibleSlots = canOverride && showUnavail ? grid : slots;

  const body = done ? (
    <div className="py-4 text-center" data-testid="rb-confirmed">
      <div className="w-12 h-12 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto mb-3"><Check className="w-6 h-6" /></div>
      <p className="font-semibold text-slate-800" data-testid="rb-confirmed-title">{done.candidate || ctxData?.name} is confirmed with {done.host || hostName}</p>
      <p className="text-sm text-slate-500 mt-1">{new Date(done.start).toLocaleString(undefined, { dateStyle: "full", timeStyle: "short" })}</p>
      {!group && <p className="text-xs text-slate-400 mt-1">You booked this appointment on {(ctxData?.name || "the candidate").split(" ")[0]}'s behalf.</p>}
      <div className="mt-4 text-left rounded-lg border border-slate-200 p-4 space-y-1.5 text-sm">
        <Row label="Type" value={type.name} />
        <Row label="Host" value={done.host || hostName} />
        <Row label="Booked by" value={done.booked_by} />
        <Row label="Attendee" value={`${ctxData?.name || ""} · ${done.attendee_email}`} />
        <Row label="Timezone" value={done.timezone} />
        <Row label="Method" value={(done.method || "").replace("_", " ")} />
        {done.meeting_link ? <Row label="Teams" value={<a href={done.meeting_link} target="_blank" rel="noreferrer" className="text-brand underline break-all">Join link</a>} />
          : done.location ? <Row label="Location" value={done.location} /> : null}
        <Row label="Portal" value={done.portal_status || "—"} />
      </div>
      <div className="mt-4 grid grid-cols-2 gap-2">
        <Button variant="outline" onClick={() => navigate("/calendar?tab=booked")} data-testid="rb-view-appointment">View Appointment</Button>
        <Button variant="outline" onClick={() => navigate(`/calendar?tab=booked&reschedule=${done.appointment_id}`)} data-testid="rb-reschedule">Reschedule</Button>
      </div>
      <Button className="mt-2 w-full bg-brand hover:bg-brand-strong" onClick={onClose} data-testid="rb-close">Done</Button>
    </div>
  ) : (
    <div className="space-y-4">
      <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 text-sm flex items-center gap-2 flex-wrap" data-testid="rb-booked-by">
        <User2 className="w-4 h-4 text-slate-400" /><span className="text-slate-500">Booked by</span>
        <span className="font-medium text-slate-800">{bookedByName}</span>
        {!group && <><span className="text-slate-300">·</span><span className="text-slate-500">Host</span><span className="font-medium text-slate-800" data-testid="rb-host-name">{hostName}</span></>}
      </div>

      <PersonPicker {...{ person, ctxData, q, setQ, results, searching, pickPerson, addLead, setAddLead, leadForm, setLeadForm, interestOptions, createLead, busy }}
        clear={() => { setPerson(null); setCtxData(null); setEmail(""); }} />

      {person && !type && (
        <div className="space-y-2" data-testid="rb-type-list">
          <Label className="text-xs font-semibold text-slate-600">Appointment type</Label>
          {types.length === 0 && <p className="text-sm text-slate-400">No appointment types are available to you for this host.</p>}
          {types.map((t) => { const I = METHOD_ICON[t.method] || Clock; return (
            <button key={t.id} data-testid={`rb-type-${t.id}`} onClick={() => setType(t)}
              className="w-full text-left rounded-lg border border-slate-200 hover:border-brand hover:bg-brand/5 p-3 transition-colors">
              <div className="flex items-center gap-2"><I className="w-4 h-4 text-brand" /><span className="font-medium text-slate-800">{t.name}</span><span className="ml-auto text-xs text-slate-400">{t.duration_min} min</span></div>
            </button>); })}
        </div>
      )}

      {person && type && (
        <>
          <div className="flex items-center justify-between">
            <div className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-700" data-testid="rb-selected-type">
              {React.createElement(METHOD_ICON[type.method] || Clock, { className: "w-4 h-4 text-brand" })}
              {type.name} · {type.duration_min}m
            </div>
            {types.length > 1 && <button onClick={() => setType(null)} className="text-xs text-slate-400 hover:text-slate-600 inline-flex items-center gap-1" data-testid="rb-change-type"><ChevronLeft className="w-3 h-3" />Change</button>}
          </div>

          {group && (
            <div data-testid="rb-host-picker">
              <Label className="text-xs font-semibold text-slate-600 mb-1 block">Host</Label>
              <Select value={hostId} onValueChange={(v) => { setHostId(v); setSlot(null); }}>
                <SelectTrigger data-testid="rb-host-select"><SelectValue placeholder="Select an eligible host" /></SelectTrigger>
                <SelectContent className="bg-white">
                  {eligibleHosts.length === 0 ? <div className="px-3 py-2 text-xs text-slate-400">No eligible hosts for this activity</div>
                    : eligibleHosts.map((h) => <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}

          <div>
            <div className="flex items-center justify-between mb-2">
              <button data-testid="rb-prev-day" onClick={() => setDate(new Date(date.getTime() - 864e5))} className="p-1.5 rounded hover:bg-slate-100"><ChevronLeft className="w-4 h-4" /></button>
              <p className="text-sm font-semibold text-slate-700" data-testid="rb-date">{date.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}</p>
              <button data-testid="rb-next-day" onClick={() => setDate(new Date(date.getTime() + 864e5))} className="p-1.5 rounded hover:bg-slate-100"><ChevronLeft className="w-4 h-4 rotate-180" /></button>
            </div>
            <p className="text-[11px] text-slate-400 mb-2 flex items-center gap-1"><CalendarDays className="w-3 h-3" />Times in {tz}</p>
            {canOverride && (
              <button type="button" onClick={() => setShowUnavail((v) => !v)} data-testid="rb-toggle-unavailable"
                className="text-[11px] font-medium text-brand hover:underline mb-2">{showUnavail ? "Hide unavailable times" : "Show unavailable times"}</button>
            )}
            {visibleSlots === null ? <p className="text-sm text-slate-400 py-4 text-center">Loading times…</p>
              : visibleSlots.length === 0 ? <p className="text-sm text-slate-400 py-4 text-center" data-testid="rb-no-slots">No open times this day.</p>
              : <div className="grid grid-cols-3 sm:grid-cols-4 gap-1.5 max-h-40 overflow-y-auto" data-testid="rb-slot-grid">
                  {visibleSlots.map((s) => {
                    const unavail = s.status && s.status !== "available";
                    return (
                      <button key={s.start} data-testid={`rb-slot-${s.start}`} onClick={() => { setSlot(s); setOverrideOk(false); }} title={s.reason || ""}
                        className={`rounded-md border py-1.5 text-sm font-medium ${slot?.start === s.start ? "border-brand bg-brand text-white"
                          : s.status === "conflict" ? "border-rose-200 bg-rose-50 text-rose-600"
                          : unavail ? "border-amber-200 bg-amber-50 text-amber-700"
                          : "border-slate-200 text-slate-700 hover:border-brand"}`}>{s.label}</button>
                    );
                  })}
                </div>}
            {slotUnavail && (
              <div className={`mt-2 rounded-md px-3 py-2 ${slot.status === "conflict" ? "bg-rose-50 border border-rose-200" : "bg-amber-50 border border-amber-200"}`} data-testid="rb-override-note">
                <p className={`text-xs font-medium ${slot.status === "conflict" ? "text-rose-700" : "text-amber-800"}`}>
                  {slot.label} — {slot.reason}{slot.conflict ? ` (${slot.conflict.start}–${slot.conflict.end})` : ""}
                </p>
                {!overrideOk ? (
                  <div className="mt-2 flex items-center gap-2 flex-wrap">
                    <Button type="button" size="sm" className={slot.status === "conflict" ? "bg-rose-600 hover:bg-rose-700" : "bg-amber-600 hover:bg-amber-700"}
                      onClick={() => setOverrideOk(true)} data-testid="rb-override-action">
                      {slot.status === "conflict" ? "Book Anyway" : "Override Availability"}
                    </Button>
                    <span className="text-[11px] text-slate-500">One-time only · won't change recurring availability.</span>
                  </div>
                ) : (
                  <p className="mt-1.5 text-xs text-emerald-700 inline-flex items-center gap-1" data-testid="rb-override-confirmed">
                    <Check className="w-3.5 h-3.5" />Override confirmed — continue to Confirm Booking.
                  </p>
                )}
              </div>
            )}
          </div>

          <div>
            <Label className="text-xs font-semibold text-slate-600 mb-1 block">Attendee email</Label>
            {(ctxData?.emails || []).length > 1 ? (
              <Select value={email} onValueChange={setEmail}>
                <SelectTrigger data-testid="rb-email-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">{ctxData.emails.map((em) => <SelectItem key={em} value={em}>{em}</SelectItem>)}</SelectContent>
              </Select>
            ) : <Input value={email} readOnly className="bg-slate-50" data-testid="rb-email" />}
            {!email && <p className="text-xs text-rose-500 mt-1">This person has no email on file.</p>}
          </div>

          {!interestHidden && (
            <div>
              <Label className="text-xs font-semibold text-slate-600 mb-1 block">Interested In{interestRequired ? "*" : ""}</Label>
              <Select value={interest} onValueChange={setInterest}>
                <SelectTrigger data-testid="rb-interested"><SelectValue placeholder="Select an option" /></SelectTrigger>
                <SelectContent className="bg-white">{interestOptions.map((o) => <SelectItem key={o.code} value={o.code}>{o.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}

          {!notesHidden && (
            <div>
              <Label className="text-xs font-semibold text-slate-600 mb-1 block">Recruiter Notes for Interviewer{notesRequired ? "*" : ""}</Label>
              <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} data-testid="rb-notes"
                placeholder="Useful context for the interviewer: prior conversations, background, goals, questions, concerns." />
              {notesRequired && <p className={`text-[11px] mt-1 ${notes.trim().length >= 20 ? "text-slate-400" : "text-amber-600"}`}>{notes.trim().length}/20 characters minimum · visible to the host, never to the candidate</p>}
            </div>
          )}

          {!resumeHidden && (
            <div>
              <Label className="text-xs font-semibold text-slate-600 mb-1 block">Resume{resumeRequired ? "*" : ""}</Label>
              <div className="flex flex-wrap items-center gap-2">
                {ctxData?.resume && (
                  <button onClick={() => setResumeMode("existing")} data-testid="rb-resume-existing"
                    className={`text-xs rounded-lg border px-2.5 py-1.5 inline-flex items-center gap-1.5 ${resumeMode === "existing" ? "border-brand text-brand bg-brand/5" : "border-slate-200 text-slate-600"}`}>
                    <FileText className="w-3.5 h-3.5" />Use existing: {ctxData.resume.filename}
                  </button>
                )}
                <input ref={fileRef} type="file" className="hidden" onChange={uploadResume} accept=".pdf,.doc,.docx" data-testid="rb-resume-input" />
                <button onClick={() => fileRef.current?.click()} data-testid="rb-resume-upload"
                  className={`text-xs rounded-lg border px-2.5 py-1.5 inline-flex items-center gap-1.5 ${resumeMode === "new" ? "border-brand text-brand bg-brand/5" : "border-slate-200 text-slate-600"}`}>
                  <Upload className="w-3.5 h-3.5" />{resumeMode === "new" && resumeName ? resumeName : "Upload new"}
                </button>
                {!resumeRequired && (
                  <button onClick={() => setResumeMode("none")} data-testid="rb-resume-none"
                    className={`text-xs rounded-lg border px-2.5 py-1.5 ${resumeMode === "none" ? "border-slate-400 text-slate-700 bg-slate-50" : "border-slate-200 text-slate-500"}`}>None</button>
                )}
              </div>
              {resumeRequired && !hasResume && <p className="text-[11px] text-amber-600 mt-1">A resume is required — use the one on file or upload a new one.</p>}
            </div>
          )}

          {slot && (
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-1 text-sm" data-testid="rb-summary">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1">Review</p>
              <Row label="Appointment" value={type.name} />
              <Row label="Host" value={hostName} />
              <Row label="Candidate" value={`${ctxData?.name || ""}${email ? ` · ${email}` : ""}`} />
              <Row label="Booked by" value={bookedByName} />
              <Row label="When" value={`${new Date(slot.start).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })} · ${tz}`} />
              <Row label="Method" value={(type.method || "").replace("_", " ")} />
            </div>
          )}

          <Button className="w-full bg-brand hover:bg-brand-strong" disabled={!canBook} onClick={book} data-testid="rb-confirm">
            {busy ? "Booking…" : "Confirm Booking"}
          </Button>
        </>
      )}
    </div>
  );

  if (inline) return <div className="rounded-xl border border-slate-200 bg-white p-5 max-w-2xl" data-testid="recruiter-booking-inline">{body}</div>;
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-white max-w-lg max-h-[90vh] overflow-y-auto" data-testid="recruiter-booking-dialog">
        <DialogHeader>
          <DialogTitle>{done ? "Meeting booked" : `Book a meeting · ${page.name}`}</DialogTitle>
          <DialogDescription className="sr-only">Book a meeting on behalf of a candidate</DialogDescription>
        </DialogHeader>
        {body}
      </DialogContent>
    </Dialog>
  );
}

function PersonPicker({ person, ctxData, q, setQ, results, searching, pickPerson, addLead, setAddLead, leadForm, setLeadForm, interestOptions, createLead, busy, clear }) {
  if (person) {
    return (
      <div>
        <Label className="text-xs font-semibold text-slate-600 mb-1 block">Candidate / Person</Label>
        <div className="rounded-lg border border-slate-200 p-2.5 flex items-start justify-between gap-2" data-testid="rb-selected-person">
          <p className="font-medium text-slate-800 text-sm truncate">{ctxData?.name || `${person.first_name} ${person.last_name}`}</p>
          <button onClick={clear} className="text-xs text-slate-400 hover:text-slate-600 shrink-0" data-testid="rb-clear-person">Change</button>
        </div>
      </div>
    );
  }
  const L = (k, ph) => <Input placeholder={ph} value={leadForm[k]} onChange={(e) => setLeadForm({ ...leadForm, [k]: e.target.value })} data-testid={`rb-lead-${k}`} />;
  return (
    <div>
      <Label className="text-xs font-semibold text-slate-600 mb-1 block">Step 1 · Who is the meeting for?</Label>
      <p className="text-[11px] text-slate-400 mb-2">Select their existing Top-Recruit record to avoid duplicates, or choose <b>Add New Lead</b>.</p>
      {!addLead ? (
        <>
          <div className="relative">
            <Search className="w-4 h-4 text-slate-400 absolute left-2.5 top-2.5" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search people by name or email…" className="pl-8" data-testid="rb-person-search" />
          </div>
          {searching && <p className="text-xs text-slate-400 mt-1">Searching…</p>}
          {results.length > 0 && (
            <div className="mt-1 border border-slate-200 rounded-lg divide-y max-h-44 overflow-y-auto" data-testid="rb-person-results">
              {results.map((p) => {
                const cls = (p.classifications || []).includes("candidate") ? "Candidate" : (p.classifications || []).includes("lead") ? "Lead" : "";
                return (
                  <button key={p.id} data-testid={`rb-person-${p.id}`} onClick={() => pickPerson(p)} className="w-full text-left px-3 py-2 hover:bg-slate-50">
                    <p className="text-sm font-medium text-slate-800">{p.first_name} {p.last_name}</p>
                    <p className="text-xs text-slate-500">{[p.email || "no email", cls, p.city].filter(Boolean).join(" · ")}</p>
                  </button>
                );
              })}
            </div>
          )}
          <div className="mt-2 flex items-center justify-between">
            <span className="text-xs text-slate-400">Can't find the person?</span>
            <Button type="button" variant="outline" size="sm" onClick={() => { setAddLead(true); setLeadForm({ first_name: "", last_name: "", email: q.includes("@") ? q : "", mobile: "", city: "", state: "", interested_in: "" }); }} data-testid="rb-add-lead-btn">
              <User2 className="w-3.5 h-3.5 mr-1.5" />Add New Lead
            </Button>
          </div>
        </>
      ) : (
        <div className="rounded-lg border border-slate-200 p-3 space-y-2.5" data-testid="rb-quick-add-lead">
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold text-slate-700">Add New Lead</p>
            <button type="button" onClick={() => setAddLead(false)} className="text-xs text-slate-400 hover:text-slate-600" data-testid="rb-add-lead-cancel">Back to search</button>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {L("first_name", "First Name*")}{L("last_name", "Last Name*")}{L("email", "Email*")}{L("mobile", "Mobile Phone")}{L("city", "City")}{L("state", "State")}
          </div>
          <Select value={leadForm.interested_in} onValueChange={(v) => setLeadForm({ ...leadForm, interested_in: v })}>
            <SelectTrigger data-testid="rb-lead-interested"><SelectValue placeholder="Interested In*" /></SelectTrigger>
            <SelectContent className="bg-white">{interestOptions.map((o) => <SelectItem key={o.code} value={o.code}>{o.label}</SelectItem>)}</SelectContent>
          </Select>
          <Button type="button" className="w-full bg-brand hover:bg-brand-strong" disabled={busy} onClick={createLead} data-testid="rb-lead-save">{busy ? "Saving…" : "Save & Select"}</Button>
          <p className="text-[11px] text-slate-400">We check for an existing record first, so no duplicate is created.</p>
        </div>
      )}
    </div>
  );
}

function Row({ label, value }) {
  return <div className="flex gap-2"><span className="text-slate-400 w-20 shrink-0">{label}</span><span className="text-slate-700 font-medium break-words">{value}</span></div>;
}
